import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CurrentUser, TenantMembership, AuthTokens } from '@maker-toolchest/shared';

interface AuthState {
  // Auth tokens
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // Unix timestamp

  // User data
  user: CurrentUser | null;
  currentTenant: TenantMembership | null;

  // Loading state
  isLoading: boolean;
  isInitialized: boolean;

  // Actions
  setTokens: (tokens: AuthTokens) => void;
  setUser: (user: CurrentUser | null) => void;
  setCurrentTenant: (tenant: TenantMembership | null) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setInitialized: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      currentTenant: null,
      isLoading: true,
      isInitialized: false,

      // Actions
      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: Date.now() + tokens.expiresIn * 1000,
        }),

      setUser: (user) =>
        set({
          user,
          currentTenant: user?.currentTenant ?? null,
        }),

      setCurrentTenant: (tenant) =>
        set({ currentTenant: tenant }),

      logout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          user: null,
          currentTenant: null,
        }),

      setLoading: (isLoading) =>
        set({ isLoading }),

      setInitialized: () =>
        set({ isInitialized: true, isLoading: false }),
    }),
    {
      name: 'maker-toolchest-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        currentTenant: state.currentTenant,
      }),
    }
  )
);

/**
 * Check if the access token is expired or about to expire
 */
export function isTokenExpired(): boolean {
  const { expiresAt } = useAuthStore.getState();
  if (!expiresAt) return true;

  // Consider expired if less than 60 seconds left
  return Date.now() > expiresAt - 60000;
}

/**
 * Get the current access token (for API requests)
 */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const { accessToken, user } = useAuthStore.getState();
  return !!accessToken && !!user;
}
